import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, Box, Checkbox, FormControlLabel, FormGroup, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useForm } from "react-hook-form";
import { LoadingButton } from '@mui/lab';
import { TickCircle } from 'iconsax-react';
import { CloseCircle } from 'iconsax-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { setMessageSnackbarStateState } from '../../../../redux/slices/common';
import { setMessage } from '../../../../redux/slices/message';
import { useWindowWidth } from '../../../../utils/browser';
import AudienceService from '../../../../services/api/audience.service';

function CreateSubCardModal({audienceId, open, categories, ageRange, interest, languages, gender, onClose, onSuccess}) {
  const browserWidth = useWindowWidth();
  const { currentTheme } = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: 'onChange'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNameAvailable, setIsNameAvailable] = useState(null);
  // const [crumbs, setCrumbs] = useState('');
  const [crumbNames, setCrumbName] = useState('');

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);

  useEffect(() => {
    const titles = [];
    
    if (categories && categories.title) titles.push(categories.title +' (Top Three)');
    if (ageRange && ageRange.title) titles.push(ageRange.title);
    if (interest && interest.title) titles.push(interest.title);
    if (languages && languages.title) titles.push(languages.title);
    if (gender && gender.title) titles.push(gender.title);

    setFilterOptions(titles);  
  }, [categories, ageRange, interest, languages, gender]);
  const getFilterOptions = () => {
    switch (selectedFilter) {
      case 'Category (Top Three)':
        return categories ? categories.data.slice(0, 3) : [];
        case 'Age Range':
          return ageRange?.charts?.map(chart => (
            {
            title: chart.name, 
            value: chart.value, 
          })) || [];
      case 'Interest':
        return interest ? interest.data.slice(0, 3) : [];
      case 'Languages':
        return languages ? languages.data.slice(0, 3) : [];
      case 'Gender':
        return gender ? gender.data : [];
      default:
        return [];
    }
  };

 const options = getFilterOptions();

 const handleCheckboxChange = (event, title, value) => {
  if (event.target.checked) {
    // Add the item (title and value) to the selected options
    setSelectedFilterOptions(prev => [...prev, { title, value }]);
  } else {
    // Remove the item (title and value) from the selected options
    setSelectedFilterOptions(prev =>
      prev.filter(item => item.title !== title || item.value !== value)
    );
  }
};

  const handleChange = (event) => {
    const name = event.target.value.toLowerCase();
    const crumbList = JSON.parse(localStorage.getItem('crumbList'));
    const crumbName = crumbList.map(crumb => crumb.name.toLowerCase());
    setCrumbName(crumbName);

    if (crumbNames.includes(name)) {
      // console.log('Audience name already taken');
      setIsNameAvailable(false);
      dispatch(setMessage({
        type: "error",
        title: "Crumb Name Is Already Taken",
      }));
      dispatch(setMessageSnackbarStateState(true));
    } else {
      // console.log('Audience name available');
      setIsNameAvailable(true);
      dispatch(setMessage({
        type: "success",
        title: "Crumb Name Is Available",
      }));
      dispatch(setMessageSnackbarStateState(true));
    }

  }

  const handleCloseDialog = () => {
    setSelectedFilter(null); 
    setSelectedFilterOptions([]); 
    reset(); 
    onClose(); 
  };

  const onSubmit = (values) => {

    const nameRegex = /^[a-zA-Z0-9\s-]+$/;
    if (!nameRegex.test(values.name)) {
      if (/\s/.test(values.name)) {
        dispatch(setMessage({
          type: "error",
          title: "Name must not contain spaces",
        }));
      } else {
        dispatch(setMessage({
          type: "error",
          title: "Name must contain only letters and hyphens",
        }));
      }
      dispatch(setMessageSnackbarStateState(true));
      return;
    }
   
    if (selectedFilterOptions.length === 0) {
      dispatch(setMessage({
        type: "warning",
        title: "No Filter Option Selected",
      }));
      dispatch(setMessageSnackbarStateState(true));
      return;
    }

    const data = {
      name: values.name.trim(),
      filter: selectedFilter,
      options: selectedFilterOptions,
      audienceId: audienceId,
    };
    setIsSubmitting(true);
    AudienceService.addSubAudience(data).then(
      (data) => {
        // console.log("data", data);
        setIsSubmitting(false);
          dispatch(setMessage({
            type: "success",
            title: data?.message ? data.message : "Sub Card added successfully",
          }));
          dispatch(setMessageSnackbarStateState(true));
          handleCloseDialog();

          if (onSuccess) {
            onSuccess(); 
          }

        return Promise.resolve();
      },
      (error) => {
        setIsSubmitting(false);
        console.log(error);
        dispatch(setMessage({
          type: "error",
          title: error.response.data.message,
        }));
        dispatch(setMessageSnackbarStateState(true));
        setIsSubmitting(false);
        return Promise.reject();
      }
    );
  };

return (<>
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{ sx: { width: "390px", borderRadius: "0px" } }}
      sx={{
        backdropFilter: "blur(25px)",
      }}
    >
      <DialogTitle textAlign="center" sx={{ p: 3, fontSize: 16, lineHeight: '20px' }}>Create A Sub Card</DialogTitle>
      <Divider />
      <Box
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ px: browserWidth > 500 ? 7.5 : 3 }}>
          <TextField
            autoFocus
            InputProps={{
              endAdornment: isNameAvailable === true ? (
                <TickCircle size="22" color="green" variant='Bold' />
              ) : isNameAvailable === false ? (
                <CloseCircle size="22" color="red" variant='Bold' />
              ) : null,
            }}
            placeholder="Sub Card Name"
            InputLabelProps={{
              style: { color: "#6F7288" }
            }}
            focused
            id="subCardName"
            type="text"
            fullWidth
            variant="standard"
            sx={{ my: 2 }}
            error={!!errors['name']}
            helperText={errors['name'] ? errors['name'].message : ''}
            {...register("name", {
              required: "Name is required"
            })}
            onChange={handleChange}
          />
         
         <Autocomplete
        fullWidth
        options={filterOptions}
        onChange={(event, newValue) => {
          setSelectedFilter(newValue || null);
          setSelectedFilterOptions([]); 
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={"Select Filter"}
            variant="standard"
            sx={{ my: 2, color: '#fff' }}
            error={!!errors.filter} 
            helperText={errors.filter ? errors.filter.message : ''} 
            {...register('filter', { required: 'Filter is required' })} 
          />
        )}
        PaperProps={{
          style: {
            maxHeight: '50vh',
            maxWidth: '50vh',
            marginTop: '2px',
            overflowX: 'auto',
          },
        }}
      />

<FormGroup>
  {options.map((item, index) => (
    <List key={item.id}>
      <ListItem disablePadding sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'start' }}>
          <FormControlLabel
            className="form-control"
            control={
              <Checkbox
                checked={selectedFilterOptions.some(
                  option => option.title === item.title && option.value === item.value
                )}
                onChange={(event) => handleCheckboxChange(event, item.title, item.value)}
                icon={<RadioButtonUncheckedIcon />} 
                checkedIcon={<CheckCircleIcon />}   
              />
            }
          />
          <ListItemText
            id={'labelId' + index}
            primary={<Typography fontSize="14px" color={'#6F7288'} sx={{ wordBreak: 'break-all' }}>{item.title}</Typography>}
            secondary={<Typography fontSize="18px" color={currentTheme === "dark" ? "#fff" : "#000"}>{item.value}</Typography>}
          />
        </Box>
      </ListItem>
    </List>
  ))}
</FormGroup>

        </DialogContent>
        <Divider />
        <DialogActions sx={{ padding: "0px" }}>
          <ButtonGroup fullWidth size='large' aria-label="outlined primary button group">
            <Button variant='flat' onClick={handleCloseDialog} style={{ textTransform: "none", borderRadius: "0px" }}>Cancel</Button>
            <LoadingButton
              variant='contained'
              fullWidth
              type='submit'
              loading={isSubmitting}
              className="form-button"
              sx={{ textTransform: "none", borderRadius: "0px" }}
            >
              Create Audience
            </LoadingButton>
          </ButtonGroup>
        </DialogActions>
      </Box>
    </Dialog>
  </>);
}

export default CreateSubCardModal;